import type { JSX } from 'react';

import { useSignedUrl } from './queries';
import type { Photo } from './schemas';
import { strings } from './strings';

/** Interpolate `{{name}}` placeholders. */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) =>
    String(values[key] ?? `{{${key}}}`),
  );
}

/**
 * One photograph, fetched through a signed URL (rule 10).
 *
 * **A deleted photograph renders as a tombstone, not a broken image.** Rule 7
 * keeps the row after the objects are gone, and an `<img>` pointed at a 410
 * would show a browser's own "broken file" icon — which tells a client their
 * app is broken rather than that their photograph was deleted on schedule.
 *
 * The `<img src>` is why this BP signs URLs at all: an image tag cannot carry
 * an `Authorization` header, and BP-11's native client authenticates with a
 * JWT.
 */
export function PhotoImage({
  photo,
  own,
  className,
}: {
  photo: Photo;
  own: boolean;
  className?: string;
}): JSX.Element {
  const signed = useSignedUrl(photo.deleted_at === null ? photo.id : '', own);

  if (photo.deleted_at !== null) {
    return (
      <div
        data-testid="photo-tombstone"
        className="flex h-full w-full flex-col items-center justify-center rounded border border-dashed border-border p-2 text-center"
      >
        <span className="text-sm font-medium">{strings.deleted}</span>
        <span className="text-xs text-muted-foreground">{strings.deletedHint}</span>
      </div>
    );
  }

  if (signed.isPending) {
    return (
      <div data-testid="photo-loading" className="h-full w-full animate-pulse rounded bg-muted" />
    );
  }
  if (signed.isError || signed.data === undefined) {
    return (
      <div role="alert" data-testid="photo-failed" className="text-sm">
        {strings.failed}
      </div>
    );
  }

  return (
    <img
      data-testid="photo-image"
      src={signed.data.url}
      alt={fill(strings.takenOn, { when: photo.taken_at.slice(0, 10) })}
      width={photo.width}
      height={photo.height}
      className={className}
    />
  );
}
